import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { CameraSource, ImageOptions } from '@capacitor/camera';
import { LongGuidBuilder } from 'apps/idl/src/modules/guid/models/long-guid-builder';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { takeUntil, tap } from 'rxjs/operators';
import { DeviceService } from '../../services/device.service';
import { SnackbarService } from '../../services/snackbar.service';
import { CameraService } from 'apps/idl/src/modules/camera/services/camera.service';

@Component({
	selector: 'di-input-file',
	templateUrl: './input-file.component.html',
	styleUrls: ['./input-file.component.scss'],
})
export class InputFileComponent extends DestroyableComponentBase implements OnInit {
	@ViewChild('fileInput', { static: true }) fileInput!: ElementRef;

	public cameraOptions: ImageOptions;
	public isMobile: boolean = false;
	private readonly moGuidBuilder = new LongGuidBuilder();

	constructor(
		private svcCamera: CameraService,
		private svcDevice: DeviceService,
		private svcSnackbar : SnackbarService
	) {
		super()
	}

	ngOnInit(): void {
		this.svcDevice.isMobile$.pipe(takeUntil(this.destroyed$)).subscribe((flag: boolean) => {
			this.isMobile = flag;
		});

		this.cameraOptions = this.svcCamera.getAndSetCameraOptions(this.cameraOptions);

	}

	@Input() title: string = '';
	@Input() isPhoto?: boolean = false;
	@Output() inputClick = new EventEmitter<File>();

	public onAddFile(event: Event) {
		event.stopPropagation();
		if (this.isMobile) {
			this.cameraOptions.source = CameraSource.Photos;
			this.svcCamera.getPicture(this.cameraOptions).pipe(
				tap((file: File) => {
					this.inputClick.emit(file)
				}
				),
				takeUntil(this.destroyed$),
			).subscribe();
		}
		else {
			this.fileInput.nativeElement.click();
		}
	}

	public takePicture(event: Event) {
		event.stopPropagation();
		this.cameraOptions.source = CameraSource.Camera;
		this.svcCamera.takePicture(this.cameraOptions).pipe(
			tap(
				(file: File) => {
					this.inputClick.emit(file)
				}
			),
			takeUntil(this.destroyed$),
		).subscribe()
	}

	public onFileSelected(event: Event) {
		const file = (event.target as HTMLInputElement).files?.[0];

		if (file) {
			const allowedExtensions = /(\.exe|\.bat|\.cmd|\.sh|\.bin|\.msi|\.dll)$/i;
			if (allowedExtensions.test(file.name)) {
				this.fileInput.nativeElement.value = '';
				return;
			}

			if (file.type === 'application/pdf') {
				const reader = new FileReader();
				reader.onload = () => {
					const arrayBuffer = reader.result as ArrayBuffer;
					const uint8Array = new Uint8Array(arrayBuffer);
					const text = new TextDecoder().decode(uint8Array.slice(0, 100));

					const versionMatch = text.match(/%PDF-(\d\.\d)/);
					if (versionMatch) {
						const version = parseFloat(versionMatch[1]);
						console.warn(`PDF version ${version}`);
						if (version < 1.4) {
							const modified = this.forcePDFVersion(uint8Array, '1.4');
							const newFile = new File([modified], file.name, { type: file.type });
							this.inputClick.emit(newFile);

						} else if (version > 1.7) {
							this.svcSnackbar.showToast(
										"warning",
										"bottom center",
										"Version du PDF est supérieur à 1.7, merci de le convertir en version 1.7 maxium.",
										null,
										null,
										null,
										"5000"
									); 							
									return;
						} else {
							this.inputClick.emit(file);
						}

					} else {
						console.warn('Version PDF non détectée → ajout %PDF-1.7');
						const modified = this.forcePDFVersion(uint8Array, '1.7');
						const newFile = new File([modified], file.name, { type: file.type });
						this.inputClick.emit(newFile);
					}
				};
				reader.readAsArrayBuffer(file);
			} else {
				this.inputClick.emit(file);
			}
		}
	}

	private forcePDFVersion(data: Uint8Array, targetVersion: string): Uint8Array {
		const header = `%PDF-${targetVersion}`;
		const encoder = new TextEncoder();
		const headerBytes = encoder.encode(header);

		for (let i = 0; i < headerBytes.length; i++) {
			data[i] = headerBytes[i];
		}

		return data;
	}

}
